import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Wallet as WalletIcon, Loader2, Images, FileDown, Presentation, ClipboardList, KeyRound, PackageOpen } from "lucide-react";
import api, { imageUrl } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import BrandName from "@/components/BrandName";
import MobileCreateButton from "@/components/MobileCreateButton";
import { toast } from "sonner";

// Same icons + labels as the All Done dashboard, so a purchased kit feels identical.
const ASSET_META = {
  pics: { label: "Photos", Icon: Images, ext: "zip" },
  pdf: { label: "PDF", Icon: FileDown, ext: "pdf" },
  pptx: { label: "Slides", Icon: Presentation, ext: "pptx" },
  worksheet: { label: "Handout", Icon: ClipboardList, ext: "pdf" },
  answer_key: { label: "Answer Key", Icon: KeyRound, ext: "pdf" },
};
// A "Full Kit" bundle purchase unlocks all of these.
const CORE_ASSETS = ["pics", "pdf", "pptx", "worksheet", "answer_key"];
const ASSET_ORDER = ["pics", "pdf", "pptx", "worksheet", "answer_key"];

export default function Wallet() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(null);

  useEffect(() => {
    api.get("/me/purchases").then(({ data }) => setData(data)).catch(() => setData({ purchases: [], count: 0, credits_spent: 0 }));
  }, []);

  // Group purchases by video → each video shows one row with its unlocked asset icons.
  const groups = useMemo(() => {
    const g = {};
    for (const p of data?.purchases || []) {
      if (!g[p.manual_id]) g[p.manual_id] = { manual_id: p.manual_id, title: p.title, hero_image_id: p.hero_image_id, assets: new Set() };
      if (p.asset === "bundle") CORE_ASSETS.forEach((a) => g[p.manual_id].assets.add(a));
      else if (ASSET_META[p.asset]) g[p.manual_id].assets.add(p.asset);
    }
    return Object.values(g).map((v) => ({ ...v, assets: ASSET_ORDER.filter((a) => v.assets.has(a)) }));
  }, [data]);

  const downloadAsset = async (manualId, asset, title) => {
    const key = `${manualId}-${asset}`;
    setBusy(key);
    try {
      const res = await api.get(`/community/${manualId}/material/${asset}`, { responseType: "blob" });
      const meta = ASSET_META[asset] || { ext: "dat" };
      const slug = (title || "stepmystep").replace(/[^a-z0-9]+/gi, "-").toLowerCase().slice(0, 40);
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `stepmystep-${slug}-${asset}.${meta.ext}`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't download — please try again.");
    } finally {
      setBusy(null);
    }
  };

  return (
    <div className="max-w-7xl mx-auto px-4 pt-3 pb-16" data-testid="wallet-page">
      <div className="flex items-center justify-between gap-2 mb-1">
        <h1 className="text-2xl sm:text-3xl font-extrabold flex items-center gap-2 min-w-0 text-white">
          <BrandName dark /> <span className="inline-flex items-center gap-2 whitespace-nowrap sms-outline-dark">PURCHASES <WalletIcon size={22} className="text-white shrink-0" /></span>
        </h1>
        <MobileCreateButton />
        {user && !user.unlimited && (
          <span className="hidden sm:inline-flex items-center gap-1.5 text-sm font-bold text-[#5d8a52] bg-[#eef3ea] px-3 py-1.5 rounded-full"><WalletIcon size={15} /> ${(((user.credits ?? 0)) / 100).toFixed(2)}</span>
        )}
      </div>
      {user && !user.unlimited && <div className="sm:hidden flex justify-center mb-2" data-testid="wallet-balance-m"><span className="inline-flex items-center gap-1.5 text-sm font-bold text-[#5d8a52] bg-[#eef3ea] px-3 py-1.5 rounded-full"><WalletIcon size={15} /> ${(((user.credits ?? 0)) / 100).toFixed(2)}</span></div>}
      <p className="text-sm text-[#e4f0e4] mb-3">Every kit you've unlocked — tap an icon to download it, anytime, free.</p>

      {!data ? (
        <div className="py-20 text-center text-[#9a96ac]"><Loader2 size={26} className="mx-auto spin" /></div>
      ) : groups.length === 0 ? (
        <div className="py-16 text-center rounded-3xl border-2 border-dashed border-[#5d8a52] bg-black text-white mt-6" data-testid="wallet-empty">
          <PackageOpen size={40} className="mx-auto mb-3 text-[#a9c39c]" />
          <p className="font-bold text-white">No purchases yet.</p>
          <p className="text-sm text-white/70 mt-1 mb-4">Unlock a creator's Full Kit from any community video — photos, PDF, slides &amp; more.</p>
          <button onClick={() => navigate("/")} className="inline-flex items-center gap-2 font-extrabold text-white px-5 py-2.5 rounded-full" style={{ background: "linear-gradient(135deg,#5d8a52,#3f6238)" }}>Browse Community</button>
        </div>
      ) : (
        <div className="mt-5 rounded-2xl border-2 border-[#5d8a52] bg-black sms-tweed overflow-hidden divide-y divide-white/10" data-testid="purchases-list">
          {groups.map((v) => (
            <div key={v.manual_id} data-testid={`purchase-row-${v.manual_id}`} className="flex items-center gap-3 px-3 py-2.5 hover:bg-white/5 transition-colors">
              {/* micro thumbnail → open the video */}
              <button onClick={() => navigate(`/community/${v.manual_id}`)} className="w-14 h-10 rounded-md bg-[#1a1a22] overflow-hidden shrink-0" title="Open video">
                {v.hero_image_id && <img src={imageUrl(v.hero_image_id)} alt="" className="w-full h-full object-cover" />}
              </button>

              <div className="flex-1 min-w-0">
                <button onClick={() => navigate(`/community/${v.manual_id}`)} className="block w-full text-left font-semibold text-sm truncate leading-tight text-white hover:text-[#8fbf82]">{v.title}</button>
                <div className="text-xs text-white/50">Full Kit — {v.assets.length} item{v.assets.length === 1 ? "" : "s"}</div>
              </div>

              {/* labeled asset icons — tap to download indefinitely */}
              <div className="flex items-end gap-1 sm:gap-2 shrink-0">
                {v.assets.map((asset) => {
                  const meta = ASSET_META[asset];
                  const Icon = meta.Icon;
                  const key = `${v.manual_id}-${asset}`;
                  return (
                    <button
                      key={asset}
                      data-testid={`download-${asset}-${v.manual_id}`}
                      onClick={() => downloadAsset(v.manual_id, asset, v.title)}
                      disabled={busy === key}
                      title={`Download ${meta.label}`}
                      className="flex flex-col items-center gap-0.5 w-12 sm:w-14 py-1.5 rounded-lg text-[#5d8a52] hover:text-[#3f6238] hover:bg-[#eef3ea] transition-colors disabled:opacity-50"
                    >
                      {busy === key ? <Loader2 size={18} className="spin" /> : <Icon size={18} />}
                      <span className="text-[10px] font-bold leading-none text-center">{meta.label}</span>
                    </button>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
